import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import Tabs from 'components/atoms/Tabs/Tabs/Tabs';
import Tab from 'components/atoms/Tabs/Tab/Tab';
import TabPanel from 'components/atoms/Tabs/TabPanel/TabPanel';
import utils from 'helpers/utils';
import Box from 'components/atoms/Layout/Box/Box';
import {H6, P} from 'components/atoms/Text/Typography/Typography';
import Toolbar from 'components/atoms/Toolbars/Toolbar/Toolbar';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import Clear from '@mui/icons-material/Clear';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import TabDivider from 'components/atoms/Dividers/TabDivider/TabDivider';
import TableViewsContent from 'components/organisms/TableViews/TableViewsContent/TableViewsContent';
import useMediaQuery from '@mui/material/useMediaQuery';
import constants from 'helpers/constants';
import StyledTableViews from 'components/organisms/TableViews/TableViews/TableViews.styles';
import {useAuthUserId} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import ListWrapper from 'components/templates/Wrappers/Headers/ListWrapper/ListWrapper';

const TableViews = React.forwardRef((props, ref) => {
  const {
    view,
    views,
    onClose,
    onShow,
    onShare,
    onSubmit,
    onDelete,
    isDirty,
    isLoading,
    onCanNotify,
    ...innerProps
  } = useComponentProps(props, 'TableViews', {
    children: ['toolbar', 'tabs', 'panels']
  });

  const userId = useAuthUserId();

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [internalState, setInternalState] = useState({
    searchValue: '',
    activeTab: false
  });

  const tableViews = useMemo(() => ({
    state: {
      ...internalState
    }
  }), [internalState]);

  const tabGroupsMemo = useMemo(() => {
    let tabGroups = [];
    if (views) {
      tabGroups.push({
        id: 0,
        name: 'active',
        title: !smDown ? 'Active view' : 'Active',
        position: 0,
        general: true,
        views: [view ?? {
          viewId: -1
        }]
      });

      tabGroups.push({
        id: 1,
        name: 'all',
        title: !smDown ? 'All views' : 'All',
        position: 1,
        general: false,
        views: []
      });

      tabGroups.push({
        id: 2,
        name: 'recent',
        title: !smDown ? 'Recent views' : 'Recent',
        position: 2,
        general: false,
        views: []
      });

      tabGroups.push({
        id: 3,
        name: 'notifications',
        title: !smDown ? 'Notifications' : 'Notifications',
        position: 3,
        general: false,
        views: []
      });

      views.forEach((view) => {
        const subscription = view.subscriptions.find((s) => +s.userId === +userId);

        if (subscription) {
          let group = tabGroups.find((tg) => tg.name === 'all');
          group.views.push(view);

          const recent = Math.max(
            (subscription.usedAt ? (new Date(subscription.usedAt)).getTime() : 0),
            (new Date(view.updatedAt)).getTime()
          ) > (Date.now() - 30 * 24 * 60 * 60 * 1000);

          if (recent) {
            group = tabGroups.find((tg) => tg.name === 'recent');
            group.views.push(view);
          }

          if (subscription.notification) {
            group = tabGroups.find((tg) => tg.name === 'notifications');
            group.views.push(view);
          }
        }
      });

      tabGroups.forEach((tg) => {
        tg.views = tg.views.filter((v) => {
          return !tableViews.state.searchValue ||
            v.name?.toLowerCase().includes(tableViews.state.searchValue);
        });
      });

      return tabGroups.sort((a, b) => a.position - b.position);
    } else {
      return null;
    }
  }, [views, tableViews.state.searchValue, view, smDown, userId]);

  const onCloseEvent = useEffectEvent(onClose);
  const closeAction = useMemo(() => ({
    label: 'Close',
    tooltip: 'Close',
    icon: Close,
    onClick: (e) => {
      onCloseEvent?.(e, 'closeButtonClick');
    }
  }), [onCloseEvent]);

  useLayoutEffect(() => {
    if (tabGroupsMemo?.length > 0) {
      let activeTab = tableViews.state.activeTab;
      if (tableViews.state.activeTab === false) {
        activeTab = tabGroupsMemo.findIndex((tg) => tg.name === 'active');
      } else if (tableViews.state.activeTab > (tabGroupsMemo?.length - 1)) {
        activeTab = (tabGroupsMemo?.length - 1);
      } else if (tableViews.state.activeTab < 0) {
        activeTab = 0;
      }

      setInternalState(utils.updater({activeTab}, true));
    }
  }, [tabGroupsMemo, tableViews.state.activeTab]);

  const hasViews = useMemo(() => {
    const tabGroup = tabGroupsMemo?.find?.((tg) => tg.id === tableViews.state.activeTab);
    return tabGroup && tabGroup.views.length > 0;
  }, [tabGroupsMemo, tableViews.state.activeTab]);

  const searchFieldRef = useRef(null);
  const internalSearchField = useMemo(() => ([{
    name: 'search',
    label: 'Search',
    inlineLabel: 'search',
    placeholder: 'Search for a view',
    type: constants.formFieldTypes.text,
    validation: constants.formFieldValidationTypes.text,
    initial: tableViews.state.searchValue,
    debounce: constants.debounce.search,
    FormFieldProps: {
      ref: searchFieldRef,
      hiddenLabel: true,
      size: 'smaller'
    },
    prefix: <Icon icon={Search} />,
    postfix: tableViews.state.searchValue?.length > 0 ? <ActionIconButton action={{
      icon: <Icon icon={Clear}/>,
      onClick: (e) => {
        setInternalState(utils.updater({
          searchValue: ''
        }, true));
        searchFieldRef.current?.focus();
        e.preventDefault();
      }
    }} /> : null
  }]), [tableViews.state.searchValue]);

  const handleActiveTabChange = (event, value) => {
    setInternalState(utils.updater({
      activeTab: value
    }, true));
  };

  const handleSearch = useCallback((field, value) => {
    setInternalState(utils.updater({
      searchValue: value
    }, true));
  }, []);

  const renderView = (view, isExpanded, isDirty, idx) => {
    return <TableViewsContent key={idx}
                              className="TableViews-view"
                              view={view}
                              isDirty={isDirty}
                              isExpanded={isExpanded}
                              tableViews={tableViews}
                              onShow={onShow}
                              onShare={onShare}
                              onDelete={onDelete}
                              onSubmit={onSubmit}
                              onCanNotify={onCanNotify}/>
  }

  return <StyledTableViews ref={ref} {...innerProps}>
    <Toolbar className="TableViews-toolbar">
      <Box className="TableViews-toolbar-title">
        <H6>Views</H6>
      </Box>
      <ActionIconButton size="smaller"
                        density="sparse"
                        variant="outlined"
                        className="TableViews-toolbar-close"
                        IconProps={{
                          size: 'smaller'
                        }}
                        action={closeAction} />
    </Toolbar>
    <Box className="TableViews-content">
      <Box className="TableViews-tabs">
        <Tabs orientation="vertical"
              value={tableViews.state.activeTab}
              onChange={handleActiveTabChange}>
          {tabGroupsMemo?.map?.((tabGroup, idx) => {
            const tab = <Tab key={tabGroup.id}
                             value={tabGroup.id}
                             label={tabGroup.title}
                             disabled={tabGroup.general && (tabGroup.views?.length ?? 0) === 0} />;
            if (idx > 0 && tabGroupsMemo[idx - 1].general && !tabGroup.general) {
              return [
                <TabDivider key={`divider_${tabGroup.id}`} fullWidth/>,
                tab
              ];
            } else {
              return tab;
            }
          })}
        </Tabs>
      </Box>
      <Box className="TableViews-panels">
        <Box className="TableViews-panels-scroll">
          <InlineForm className="TableViews-search"
                      onChange={handleSearch}
                      fields={internalSearchField} />

          {!hasViews ? <Box className="TableViews-empty">
            <P>No views found</P>
          </Box> : null}

          {tabGroupsMemo?.map((tabGroup) => {
            return <TabPanel key={tabGroup.id}
                             className="TableViews-panel"
                             value={tableViews.state.activeTab}
                             index={tabGroup.id}>
              <ListWrapper>
                {tabGroup.views.map((v, idx) => {
                  const current = v.viewId === -1 || +view?.viewId === +v.viewId;
                  const expanded = current && tabGroup.general;
                  const dirty = current && isDirty;
                  return renderView(v, expanded, dirty, idx);
                })}
              </ListWrapper>
            </TabPanel>
          })}
        </Box>
      </Box>
    </Box>
  </StyledTableViews>
});

TableViews.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  view: PropTypes.object,
  views: PropTypes.array,
  onClose: PropTypes.func,
  onShow: PropTypes.func,
  onShare: PropTypes.func,
  onDelete: PropTypes.func,
  onSubmit: PropTypes.func,
  isDirty: PropTypes.bool,
  isLoading: PropTypes.bool,
  onCanNotify: PropTypes.func
};

export default TableViews;
